import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { projects } from "@/data/projects";
import { Reveal } from "@/components/Motion";

// Asymmetric bento spans keep the grid editorial rather than uniform.
const spans = [
  "md:col-span-7 aspect-[4/3]",
  "md:col-span-5 aspect-[4/3] md:aspect-auto",
  "md:col-span-5 aspect-[4/3] md:aspect-auto",
  "md:col-span-7 aspect-[4/3]",
];

const SLIDE_INTERVAL = 6000;
const STAGGER_DELAY = 700;
const canPauseForPointer = () =>
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const Card = ({ project, spanClass, index }) => {
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const [activeImage, setActiveImage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const imageRefs = useRef([]);
  const images = project.images;
  const hasSlideshow = images.length > 1 && !shouldReduceMotion;
  const displayedActiveImage = shouldReduceMotion ? 0 : activeImage;
  const isPaused = isHovered || hasFocusWithin;

  useEffect(() => {
    if (!hasSlideshow || isPaused) {
      return undefined;
    }

    const showNextImage = () => {
      setActiveImage((currentImage) => {
        const nextImage = (currentImage + 1) % images.length;
        const nextImageElement = imageRefs.current[nextImage];

        return nextImageElement?.complete && nextImageElement.naturalWidth > 0
          ? nextImage
          : currentImage;
      });
    };

    let intervalId;
    const timeoutId = window.setTimeout(() => {
      showNextImage();
      intervalId = window.setInterval(showNextImage, SLIDE_INTERVAL);
    }, SLIDE_INTERVAL + index * STAGGER_DELAY);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [hasSlideshow, images, index, isPaused]);

  return (
    <Reveal delay={(index % 2) * 0.08} className={spanClass}>
      <button
        data-testid={`realisatie-${project.slug}`}
        onClick={() => navigate(`/realisaties/${project.slug}`)}
        onMouseEnter={() => {
          if (canPauseForPointer()) setIsHovered(true);
        }}
        onMouseLeave={() => {
          if (canPauseForPointer()) setIsHovered(false);
        }}
        onFocus={() => {
          if (canPauseForPointer()) setHasFocusWithin(true);
        }}
        onBlur={(event) => {
          if (
            canPauseForPointer() &&
            !event.currentTarget.contains(event.relatedTarget)
          ) {
            setHasFocusWithin(false);
          }
        }}
        className="group block w-full h-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-4 focus-visible:ring-offset-offwhite"
      >
        <div className="relative overflow-hidden rounded-2xl md:rounded-3xl h-full min-h-[280px] shadow-[0_16px_40px_rgba(45,51,47,0.08)] transition-shadow duration-500 group-hover:shadow-[0_22px_55px_rgba(45,51,47,0.13)]">
          <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-105">
            {(shouldReduceMotion ? images.slice(0, 1) : images).map((image, imageIndex) => {
              const isActive = imageIndex === displayedActiveImage;

              return (
                <img
                  key={image.src}
                  ref={(element) => {
                    imageRefs.current[imageIndex] = element;
                  }}
                  src={image.src}
                  alt={image.alt}
                  aria-hidden={!isActive}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity ease-in-out ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                  style={{
                    objectPosition: image.objectPosition,
                    transitionDuration: "900ms",
                  }}
                  loading="lazy"
                />
              );
            })}
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
          <div className="absolute inset-0 flex items-end justify-between p-6 md:p-8">
            <div>
              <p className="font-body text-cream/80 text-xs tracking-widest uppercase mb-2">
                {project.location} · {project.type}
              </p>
              <h3 className="font-heading font-normal text-cream text-2xl md:text-3xl tracking-tight">
                {project.name}
              </h3>
            </div>
            <span className="shrink-0 h-11 w-11 rounded-full border border-white/50 bg-ink/10 backdrop-blur-sm grid place-items-center text-white opacity-100 transition-all duration-300 md:translate-y-2 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100">
              <ArrowUpRight size={18} />
            </span>
          </div>
        </div>
      </button>
    </Reveal>
  );
};

const Realisaties = () => {
  return (
    <section id="realisaties" className="bg-offwhite py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <Reveal className="max-w-3xl mb-14 md:mb-20">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-forest" />
            <span className="font-body text-xs tracking-[0.25em] uppercase text-forest">
              Realisaties
            </span>
          </div>
          <h2 className="font-heading font-light tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-ink text-balance">
            Een selectie van woningen en projecten die we met zorg realiseerden.
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-12 gap-4 md:gap-6">
          {projects.map((p, i) => (
            <Card
              key={p.slug}
              project={p}
              index={i}
              spanClass={spans[i] || "md:col-span-6 aspect-[4/3]"}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Realisaties;
